
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Download, TrendingUp, TrendingDown, Minus, FileText, Calendar, BarChart3 } from 'lucide-react';
import { PDFUtils } from '@/utils/pdfUtils';
import { useDownloadGate } from '@/components/DownloadGate/useDownloadGate';

interface ChartDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  data: any[];
  category: string;
  clickedValue?: number;
  clickedLabel?: string;
}

export const ChartDetailModal = ({ 
  isOpen, 
  onClose, 
  title, 
  data, 
  category, 
  clickedValue, 
  clickedLabel 
}: ChartDetailModalProps) => {
  const { guardDownload } = useDownloadGate();

  const handleDownload = async (format: 'csv' | 'pdf') => {
    if (!(await guardDownload())) return;

    const currentDate = new Date().toLocaleDateString();
    const currentYear = new Date().getFullYear();
    
    if (format === 'csv') {
      const headers = ['Name/Activity', 'Value', 'Unit', 'Scope', 'Category', 'Source', 'Status', 'Date', 'Calculation Type'];
      const csvContent = [
        headers.join(','),
        ...data.map(item => [
          item.activity_name || item.name || 'N/A',
          item.value || 'N/A',
          item.unit || 'N/A',
          item.scope || 'N/A',
          item.category || 'N/A',
          item.source || 'N/A',
          item.status || 'N/A',
          item.date || 'N/A',
          item.calculation_type || 'N/A'
        ])
      ].map(row => row.join(',')).join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title.toLowerCase().replace(/\s+/g, '_')}_detailed_data_${currentDate.replace(/\//g, '-')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } else if (format === 'pdf') {
      try {
        const blob = await PDFUtils.createCompletePDF(
          {
            title: `${title} - Detailed Analysis`,
            subtitle: 'Performance Analysis Report',
            exportType: 'chart_analysis',
            includeLogo: true,
            includeFooter: true,
            includeSummary: true,
            summaryData: {
              totalRecords: data.length,
              exportFormat: 'PDF',
              additionalInfo: [
                `Generated on: ${currentDate}`,
                `Category: ${category}`,
                clickedValue && clickedLabel ? `Selected: ${clickedLabel} - ${clickedValue}` : 'No specific data point selected'
              ]
            }
          },
          (pdf, yPosition, pageNum) => {
            // Add performance summary
            pdf.setFontSize(14);
            pdf.setFont(undefined, 'bold');
            pdf.text('Performance Summary:', 20, yPosition);
            pdf.setFont(undefined, 'normal');
            
            yPosition += 15;
            pdf.setFontSize(12);
            
            const totalValue = data.reduce((sum, item) => sum + (item.value || 0), 0);
            const avgValue = Math.round(totalValue / data.length) || 0;
            const verifiedCount = data.filter(item => item.status === 'verified').length;
            
            pdf.text(`Total Value: ${totalValue.toLocaleString()}`, 20, yPosition);
            yPosition += 10;
            pdf.text(`Data Points: ${data.length}`, 20, yPosition);
            yPosition += 10;
            pdf.text(`Average: ${avgValue}`, 20, yPosition);
            yPosition += 10;
            pdf.text(`Verified: ${verifiedCount}`, 20, yPosition);
            
            yPosition += 20;
            
            // Add data table
            pdf.setFontSize(12);
            pdf.setFont(undefined, 'bold');
            pdf.text('Detailed Data:', 20, yPosition);
            pdf.setFont(undefined, 'normal');
            
            yPosition += 15;
            pdf.setFontSize(10);
            
            // Table headers
            pdf.text('Name/Activity', 20, yPosition);
            pdf.text('Value', 80, yPosition);
            pdf.text('Unit', 110, yPosition);
            pdf.text('Scope', 130, yPosition);
            pdf.text('Category', 160, yPosition);
            pdf.text('Source', 190, yPosition);
            pdf.text('Status', 220, yPosition);
            
            yPosition += 10;
            
            // Table data
            data.forEach((item, index) => {
              const { yPosition: newY, pageNum: newPageNum } = PDFUtils.handlePageBreak(pdf, yPosition, pageNum, 250);
              yPosition = newY;
              pageNum = newPageNum;
              
              pdf.text((item.activity_name || item.name || 'N/A').substring(0, 15), 20, yPosition);
              pdf.text(String(item.value || 0), 80, yPosition);
              pdf.text(item.unit || 'N/A', 110, yPosition);
              pdf.text(item.scope || 'N/A', 130, yPosition);
              pdf.text(item.category || 'N/A', 160, yPosition);
              pdf.text(item.source || 'N/A', 190, yPosition);
              pdf.text(item.status || 'N/A', 220, yPosition);
              
              yPosition += 10;
            });
            
            return { yPosition, pageNum };
          }
        );
        
        // Create download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${title.toLowerCase().replace(/\s+/g, '_')}_analysis.pdf`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error generating PDF:', error);
      }
    }
  };

  const renderDataTable = (items: any[]) => (
    <div className="space-y-4">
      {/* Table Summary */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Showing {items.length} data points
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-600">Total Value:</span>
          <span className="font-medium">
            {items.reduce((sum, item) => sum + (item.value || 0), 0).toLocaleString()} {items[0]?.unit || ''}
          </span>
        </div>
      </div>
      
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name/Activity</TableHead>
            <TableHead>Value</TableHead>
            <TableHead>Unit</TableHead>
            <TableHead>Scope</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Date</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item, index) => (
            <TableRow key={index}>
              <TableCell className="font-medium">
                {item.activity_name || item.name || 'N/A'}
              </TableCell>
              <TableCell className="font-bold text-green-600">
                {item.value?.toLocaleString()}
              </TableCell>
              <TableCell>{item.unit}</TableCell>
              <TableCell>
                {item.scope && (
                  <Badge variant="outline" className="text-xs">
                    {item.scope}
                  </Badge>
                )}
              </TableCell>
              <TableCell>
                <Badge variant="secondary" className="text-xs">
                  {item.category}
                </Badge>
              </TableCell>
              <TableCell className="text-sm text-gray-600">{item.source}</TableCell>
              <TableCell>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  item.status === 'verified' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {item.status}
                </span>
              </TableCell>
              <TableCell className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="h-3 w-3" />
                {item.date || 'N/A'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            {title} - Detailed Breakdown
          </DialogTitle>
          <DialogDescription>
            Comprehensive analysis of {category} data with downloadable insights
            {clickedValue && clickedLabel && (
              <span className="block mt-2 text-sm font-medium">
                Selected: {clickedLabel} - Value: {clickedValue}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Clicked Value Summary */}
          {clickedValue && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Selected Data Point Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{clickedValue}</div>
                    <div className="text-sm text-gray-500">Current Score</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{clickedLabel}</div>
                    <div className="text-sm text-gray-500">Period</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{category}</div>
                    <div className="text-sm text-gray-500">Category</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">
                      {data.length}
                    </div>
                    <div className="text-sm text-gray-500">Data Points</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Download Options */}
          <div className="flex gap-2">
            <Button onClick={() => handleDownload('csv')} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Download CSV
            </Button>
            <Button onClick={() => handleDownload('pdf')} variant="outline" size="sm">
              <FileText className="h-4 w-4 mr-2" />
              Download PDF Report
            </Button>
          </div>

          {/* Data Breakdown Tabs */}
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="environmental">Environmental</TabsTrigger>
              <TabsTrigger value="social">Social</TabsTrigger>
              <TabsTrigger value="governance">Governance</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 border rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {data.reduce((sum, item) => sum + (item.value || 0), 0).toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-500">Total Value</div>
                </div>
                <div className="p-4 border rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{data.length}</div>
                  <div className="text-sm text-gray-500">Data Points</div>
                </div>
                <div className="p-4 border rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">
                    {Math.round((data.reduce((sum, item) => sum + (item.value || 0), 0) / data.length) || 0)}
                  </div>
                  <div className="text-sm text-gray-500">Average</div>
                </div>
                <div className="p-4 border rounded-lg">
                  <div className="text-2xl font-bold text-orange-600">
                    {data.filter(item => item.status === 'verified').length}
                  </div>
                  <div className="text-sm text-gray-500">Verified</div>
                </div>
              </div>
              {renderDataTable(data)}
            </TabsContent>

            <TabsContent value="environmental">
              {renderDataTable(data.filter(item => item.category === 'environmental' || item.category === 'emissions' || item.category === 'energy'))}
            </TabsContent>

            <TabsContent value="social">
              {renderDataTable(data.filter(item => item.category === 'social'))}
            </TabsContent>

            <TabsContent value="governance">
              {renderDataTable(data.filter(item => item.category === 'governance'))}
            </TabsContent>
          </Tabs>

          {/* Breakdown Details */}
          {data.length > 0 && data[0].breakdown && (
            <Card>
              <CardHeader>
                <CardTitle>Component Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(data[0].breakdown).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-center p-2 border rounded">
                      <span className="font-medium">{key}</span>
                      <span className="text-lg font-bold">{value as number}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="flex justify-end">
          <Button onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
